import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, css, fonts, frames, getGraphColor, glyphs, Units } from '../../../../Styles';
import { ButtonList, PageHeader } from '../../../../Components';
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types } from '../../../../Definitions'
import { Graph_QL, useFeedComponents } from '../../../../Services/Agrisys_GqlDataCaller';
import { useAppSelector } from '../../../../Store';
import { ReactiveTable } from '../../../../Components/Tables/reactiveTableRenderer';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel } from '@tanstack/react-table';
import { keyBy, merge } from 'lodash';
import { GenericDataParameters } from '../../../../Components/Tables/_Tables/useColumnConstructor';
import { FeedComponentInfo } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedComponentsCall';
import { useEditableColumnConstructor } from '../../../../Components/Tables/_Tables/useEditableColumnConstructor';
import { Pages } from '../../../../Navigation/Routers/root';
import { ValueSteps } from '../../../../Components/BaseComponents/newInput';

const getComponentTableDivStyle = (id?: string | number):CSSProperties|undefined => { if (!id) {return undefined} return {backgroundColor: getGraphColor(+id), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}}
interface TableData {
  id: number,
  [key: string]: string|number|boolean|undefined
}

const getUnitFromAccessor = (accessor: keyof FeedComponentInfo) => {
  if (accessor === "ds" || accessor === "protein") {
    return Units.pct_white
  }
  if (accessor === "energy") {
    return Units.kG_white
  }
  return 
}

export default function Components() {
  const {t} = useTranslation();
  const [editTable, setEditTable] = useState(false);
  const [addedRowCounter, setAddedRowCount] = useState(1);
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const feedComponentAccessorKeys = useMemo(() => ({
    ...(editTable ? { delete: ListData_ValueTypes.DeleteRowAction } : {}),
    name: ListData_ValueTypes.Div,
    readyMixture: ListData_ValueTypes.Check,
    priority: ListData_ValueTypes.Number,
    ds: ListData_ValueTypes.Number,
    energy: ListData_ValueTypes.Number,
    protein: ListData_ValueTypes.Number,
  }), [editTable]);
  
  const {call: getFeedComponentsInfo, data: feedComponentsState, maintainEntries} = useFeedComponents(connection);
 
  // Switch between edit mode and non-edit mode
  const switchTable = () => {
    setEditTable((editTable) => !editTable);
  };

  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data: undefined,[domain, feedComponentsState])
  const [data, setData] = useState(feedComponents?.filter((component) => !component.deleted) ?? []); // TODO CTO check if we aren't already filtering deleted components (we should be)

  useEffect(() => {
    if (feedComponents) {
      setData(feedComponents.filter((component) => !component.deleted));
    }
  },[feedComponents])
  const dataRef = useRef<TableData[]>([]);

  const saveChangedData = () => {
    if (maintainEntries) {
      const data = dataRef.current.map((tableData) => {
        const foundFeedComponents = feedComponents?.find((component) => component.id === tableData.id);
        if (foundFeedComponents === undefined) {
          const defaultValues: FeedComponentInfo = {id: -1, name: "Feed Component", readyMixture: false, priority: 1, ds: 0, energy: 0, protein: 0, deleted: false}
          const suggestedDataEntry = { ...defaultValues, ...tableData };
          return suggestedDataEntry;
        }
        const suggestedDataEntry = {...foundFeedComponents, ...tableData };
        delete suggestedDataEntry["__typename"];
        return suggestedDataEntry;
      });
      maintainEntries({con: connection.connection, data: data});
      dataRef.current = [];
    }
  }

  const clearDirt = useCallback(() => {
    if (feedComponents) {
      setData(feedComponents.filter((component) => !component.deleted));
      dataRef.current = [];
    }
  },[feedComponents])

  const addRow = useCallback(() => {
    setEditTable(true);
    setAddedRowCount(addedRowCounter + 1);
    setData([{id: -addedRowCounter, name: "", readyMixture: false, priority: 0, ds: 0, energy: 0, protein: 0, deleted: false}, ...Object.values(merge([], keyBy(data, "id"), keyBy(dataRef.current, "id"))).filter((row) => !row.deleted)]);
  },[addedRowCounter, data])

  useEffect(() => {
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const accessorData = useMemo<GenericDataParameters<FeedComponentInfo>[]>(() => {
    return Object.entries(feedComponentAccessorKeys).map(([accessor, type]) => {
      return {
        headerValue: t(`feedCurvePages.components.${accessor}`, {defaultValue: type}),
        accessor,
        type,
        unit: getUnitFromAccessor(accessor as keyof FeedComponentInfo),
        width: accessor ==="delete" ? "40px" : accessor ==="name" ? "200px" : undefined,
        sort: accessor ==="delete" ? false : true,
        divStyleOverride: type === ListData_ValueTypes.Div ? getComponentTableDivStyle : undefined,
        styleOverrideOnValue: false,
        valueStep: type === ListData_ValueTypes.Number && accessor !== "priority" ? ValueSteps.floats : undefined,
      }
    })
  },[feedComponentAccessorKeys, t]);
  const columns = useEditableColumnConstructor<FeedComponentInfo>({accessorData, tableStyle: TableStyles_Types.primaryTable, canEdit: editTable})
  
  const table = useReactTable({
    columns: columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      updateData: (id, columnId, value) => {
        dataRef.current = (Object.values(merge(keyBy(dataRef.current, "id"), keyBy([{id, [columnId]: value}], "id"))))
      },
      deleteRow: (deletedRowId) => {
        dataRef.current = Object.values(merge(keyBy(dataRef.current, "id"),keyBy([{id: deletedRowId, deleted: true}], "id")));
        setData(Object.values(merge([], keyBy(data, "id"), keyBy(dataRef.current, "id"))).filter((row) => !row.deleted));
      }
    },
    state: {
      columnPinning: {
        left: !editTable ? ["name"] : []
      }
    }
  })

  if (!feedComponents) { return (<div></div>)}
  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <PageHeader topHeader={t(`menuHeaders.${Pages.feedCurves}`, {defaultValue: "feed curves"})} subHeader={t(`menuHeaders.${Pages.components}`, {defaultValue: "components"})}/>
      </frames._page_inner_header>
      <frames._page_inner_content>
        <div style={{height: "100%", width: "100%", display: "grid", gridAutoRows: "50px 1fr", boxSizing: "border-box"}}>
          <div style={{ backgroundColor: "transparent", display: "flex", justifyContent: "end", padding: "0px 20px", gap: "5px"}}>
            {!editTable? 
              <ButtonList 
                contents={[t("buttons.add", {defaultValue: "add"}), t("buttons.edit", {defaultValue: "edit"})]} 
                onClick={(i => {
                  if (i === 0) addRow()
                  else if (i === 1) switchTable()
                })}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[glyphs.Add({}), glyphs.Edit({})]}
              />
            : <ButtonList 
                contents={[t("buttons.save", {defaultValue: "save"}), t("buttons.cancel", {defaultValue: "cancel"}), t("buttons.add", {defaultValue: "add"}), t("buttons.edit", {defaultValue: "edit"})]} 
                onClick={(i => {
                  if (i === 0) { saveChangedData(); switchTable() }
                  else if (i === 1) { clearDirt(); switchTable() }
                })}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[glyphs.Check({}), glyphs.Close({}), glyphs.Add({}), glyphs.Edit({})]}
                disabled={[false, false, true, true]}
                overrides={(index) => ({
                  baseButton: {
                    backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary
                  },
                  disabledStyles: {
                    opacity: index === 3 ? 1 : 0.5
                  }
                })}
              />
            }
          </div>
          <div style={{height: "fit-content", maxHeight: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid "+colors.greyPrimary, borderRadius: "10px", overflow: "hidden"}}>
            {<ReactiveTable table={table} tableType={TableStyles_Types.primaryTable} emptyText={t("feedCurvePages.components.emptyText", {defaultValue: "No components found"})}/>}
          </div>
        </div>
      </frames._page_inner_content>
    </frames.Page_inner>
  );
}